import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useClient, usePages, useReviewSummary, useUpdateClient } from '@/api/queries'
import { ApiError } from '@/api/errors'
import { PageHeader } from '@/components/app/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field, Input } from '@/components/ui/Field'
import { SkeletonRows } from '@/components/ui/States'
import { useToast } from '@/components/ui/Toast'
import { RepeatableList } from './RepeatableList'
import { CrawlConfigCard, type CrawlConfigValue } from './CrawlConfigCard'
import { dateTime } from '@/lib/format'
import type { Client } from '@/types'

type Draft = Pick<
  Client,
  | 'name'
  | 'primaryDomain'
  | 'additionalDomains'
  | 'excludePatterns'
  | 'pageLimit'
  | 'cadence'
>

function draftFrom(client: Client): Draft {
  return {
    name: client.name,
    primaryDomain: client.primaryDomain,
    additionalDomains: client.additionalDomains,
    excludePatterns: client.excludePatterns,
    pageLimit: client.pageLimit,
    cadence: client.cadence,
  }
}

/** Trim, then drop empties — see the note in RepeatableList. */
function clean(values: string[]): string[] {
  return values.map((v) => v.trim()).filter(Boolean)
}

/**
 * Edit-only. There is no client creation and no delete here — clients are
 * provisioned and retired out of band (docs/03-page-specs.md §3). Include
 * patterns and depth limit stay on the record but are not edited here until
 * the crawl consumes them (APP-54).
 */
export function ClientProfilePage() {
  const { clientId } = useParams()
  const { data: client, isLoading } = useClient(clientId!)
  const { data: pages = [] } = usePages(clientId!)
  const { data: summary } = useReviewSummary(clientId!)
  const update = useUpdateClient(clientId!)
  const toast = useToast()

  const [draft, setDraft] = useState<Draft | null>(null)

  // Adopt the server record once, and again whenever the client changes
  // underneath — but never while there are unsaved edits in flight.
  useEffect(() => {
    if (client && !draft) setDraft(draftFrom(client))
  }, [client, draft])

  const dirty = useMemo(() => {
    if (!client || !draft) return false
    return JSON.stringify(draft) !== JSON.stringify(draftFrom(client))
  }, [client, draft])

  // Losing a half-edited crawl config to an accidental tab close is cheap to
  // prevent and annoying to hit.
  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  if (isLoading || !client || !draft) {
    return (
      <Card>
        <SkeletonRows rows={6} cols={2} />
      </Card>
    )
  }

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d))

  async function save() {
    if (!draft) return
    await update.mutateAsync({
      ...draft,
      name: draft.name.trim(),
      // The API takes no null primaryDomain; left blank, it is not sent.
      primaryDomain: draft.primaryDomain?.trim() || undefined,
      additionalDomains: clean(draft.additionalDomains),
      excludePatterns: clean(draft.excludePatterns),
    })
    toast.push('Client profile saved.')
  }

  async function trySave() {
    try {
      await save()
    } catch (cause) {
      toast.push(cause instanceof ApiError ? cause.message : 'The profile was not saved.', 'error')
    }
  }

  const crawlConfig: CrawlConfigValue = {
    excludePatterns: draft.excludePatterns,
    pageLimit: draft.pageLimit,
    cadence: draft.cadence,
  }

  // Null until the summary loads, or while the API does not serve it.
  const entityTotal = summary
    ? summary.programs + summary.quotes + summary.media + summary.facts
    : null

  return (
    <>
      <PageHeader
        title="Client profile"
        description="Identity and crawl scope for this client. Crawl changes take effect on the next run."
      />

      <div className="max-w-[720px] space-y-5 pb-20">
        <Card>
          <CardHeader title="Identity" />
          <CardBody className="space-y-5">
            <Field label="Client name" required>
              {(p) => <Input {...p} value={draft.name} onChange={(e) => set({ name: e.target.value })} />}
            </Field>

            <Field label="Primary domain" required hint="Hostname only, no scheme.">
              {(p) => (
                <Input
                  {...p}
                  value={draft.primaryDomain ?? ''}
                  className="font-mono text-[13px]"
                  onChange={(e) => set({ primaryDomain: e.target.value })}
                />
              )}
            </Field>

            <RepeatableList
              label="Additional domains and subdomains"
              values={draft.additionalDomains}
              onChange={(additionalDomains) => set({ additionalDomains })}
              placeholder="news.example.com"
              addLabel="Add domain"
              mono
            />
          </CardBody>
        </Card>

        <CrawlConfigCard
          value={crawlConfig}
          onChange={set}
          crawledUrls={pages.map((p) => p.url)}
        />

        <Card>
          <CardHeader title="Metadata" description="Read-only." />
          <CardBody>
            <dl className="grid gap-x-6 gap-y-3 text-[13px] sm:grid-cols-2">
              <div>
                <dt className="field-label">Created</dt>
                <dd className="mt-0.5 text-ink-muted">{dateTime(client.createdAt)}</dd>
              </div>
              <div>
                <dt className="field-label">Last modified</dt>
                <dd className="mt-0.5 text-ink-muted">{dateTime(client.updatedAt)}</dd>
              </div>
              <div>
                <dt className="field-label">Last crawl run</dt>
                <dd className="mt-0.5 font-mono text-[12px] text-ink-muted">
                  {client.lastCrawlRunId ?? 'never crawled'}
                </dd>
              </div>
              <div>
                <dt className="field-label">Entities awaiting review</dt>
                <dd className="tabular mt-0.5 text-ink-muted">{entityTotal ?? '—'}</dd>
              </div>
            </dl>
          </CardBody>
        </Card>
      </div>

      {/* Pinned save bar. Appears only when there is something to save. */}
      {dirty && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-rule bg-surface px-6 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="mx-auto flex max-w-[1440px] items-center gap-3">
            <span className="text-[13px] text-ink-muted">Unsaved changes.</span>
            <span className="ml-auto flex gap-2">
              <Button size="sm" onClick={() => setDraft(draftFrom(client))}>
                Discard
              </Button>
              <Button size="sm" variant="primary" loading={update.isPending} onClick={() => void trySave()}>
                Save changes
              </Button>
            </span>
          </div>
        </div>
      )}
    </>
  )
}
